import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPhone, 
  faEnvelope, 
  faMapMarkerAlt,
  faShieldAlt,
  faFileContract,
  faGavel,
  faRobot
} from '@fortawesome/free-solid-svg-icons';
import KairosLogo from '../../../assets/img/logos/KairosIQLogov2.jpg';

interface FooterWithPoliciesProps {
  tenant?: any;
  branding?: any;
  isWhiteLabel?: boolean;
}

const FooterWithPolicies: React.FC<FooterWithPoliciesProps> = ({ tenant, branding, isWhiteLabel }) => {
  return (
    <footer className="chat-footer-enhanced">
      <Container>
        {/* Main Footer Content */}
        <Row className="footer-main py-5">
          {/* Company Info */}
          <Col md={4} className="mb-4">
            <div className="footer-brand">
              <img 
                src={KairosLogo} 
                alt="KairosIQ AI Realty" 
                style={{ 
                  height: '50px', 
                  marginBottom: '15px',
                  objectFit: 'contain'
                }}
              />
              <h5>{isWhiteLabel && tenant ? tenant.companyName : 'KairosIQ AI Realty'}</h5>
              <p className="text-muted">
                {isWhiteLabel && tenant ? 
                  `Licensed Real Estate Brokerage providing exclusive access to MLS listings. Enhanced with AI technology for intelligent property matching.` :
                  `Licensed Real Estate Brokerage providing exclusive access to MLS listings. Enhanced with KairosIQ AI technology for intelligent property matching.`
                }
              </p>
              <div className="contact-info">
                {isWhiteLabel && tenant ? (
                  <>
                    {tenant.contactPhone && <p><FontAwesomeIcon icon={faPhone} /> {tenant.contactPhone}</p>}
                    {tenant.contactEmail && <p><FontAwesomeIcon icon={faEnvelope} /> {tenant.contactEmail}</p>}
                    <p><FontAwesomeIcon icon={faMapMarkerAlt} /> Arizona</p>
                  </>
                ) : (
                  <>
                    <p><FontAwesomeIcon icon={faPhone} /> 1-602-860-6110</p>
                    <p><FontAwesomeIcon icon={faEnvelope} /> michael@kairosiq.ai</p>
                    <p><FontAwesomeIcon icon={faMapMarkerAlt} /> 2700 S Gilbert Rd, Suite 5, Chandler, AZ 85286</p>
                  </>
                )}
              </div>
            </div>
          </Col>

          {/* Quick Links */}
          <Col md={2} className="mb-4">
            <h6 className="footer-title">Quick Links</h6>
            <ul className="footer-links">
              <li><Link to="/search">Property Search</Link></li>
              <li><Link to="/home-value">Home Valuation</Link></li>
              <li><Link to="/find-agent">Find an Agent</Link></li>
              <li><Link to="/mortgage-calculator">Mortgage Calculator</Link></li>
              <li><Link to="/neighborhood-guide">Neighborhood Guide</Link></li>
            </ul>
          </Col>

          {/* Legal */}
          <Col md={3} className="mb-4">
            <h6 className="footer-title">Legal & Compliance</h6>
            <ul className="footer-links">
              <li>
                <Link to="/privacy-policy">
                  <FontAwesomeIcon icon={faShieldAlt} /> Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms-of-service">
                  <FontAwesomeIcon icon={faFileContract} /> Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/ai-ethics">
                  <FontAwesomeIcon icon={faRobot} /> AI Ethics Policy
                </Link>
              </li>
              <li>
                <Link to="/fair-housing">
                  <FontAwesomeIcon icon={faGavel} /> Fair Housing Statement
                </Link>
              </li>
              <li>
                <a href="https://www.nar.realtor/about-nar/governing-documents/code-of-ethics" target="_blank" rel="noopener noreferrer">
                  REALTOR® Code of Ethics
                </a>
              </li>
            </ul>
          </Col>

          {/* Fair Housing */}
          <Col md={3} className="mb-4">
            <h6 className="footer-title">Equal Housing Opportunity</h6>
            <div className="fair-housing-section">
              {/* Fair Housing Logo SVG */}
              <svg 
                className="fair-housing-logo mb-3" 
                viewBox="0 0 100 100" 
                xmlns="http://www.w3.org/2000/svg"
                aria-label="Equal Housing Opportunity"
                style={{ width: '80px', height: '80px' }}
              >
                {/* House */}
                <path d="M50 15 L20 40 L20 70 L40 70 L40 50 L60 50 L60 70 L80 70 L80 40 Z" 
                      fill="#1a1a1a" 
                      stroke="#1a1a1a" 
                      strokeWidth="2"/>
                {/* Roof */}
                <path d="M50 15 L80 40 M50 15 L20 40" 
                      fill="none" 
                      stroke="#1a1a1a" 
                      strokeWidth="3"/>
                {/* Equal sign */}
                <rect x="85" y="35" width="12" height="3" fill="#1a1a1a"/>
                <rect x="85" y="45" width="12" height="3" fill="#1a1a1a"/>
                {/* Text */}
                <text x="50" y="85" 
                      textAnchor="middle" 
                      fontSize="8" 
                      fill="#1a1a1a"
                      fontFamily="Arial, sans-serif"
                      fontWeight="bold">
                  EQUAL HOUSING
                </text>
                <text x="50" y="94" 
                      textAnchor="middle" 
                      fontSize="8" 
                      fill="#1a1a1a"
                      fontFamily="Arial, sans-serif"
                      fontWeight="bold">
                  OPPORTUNITY
                </text>
              </svg>
              
              <p className="fair-housing-text text-muted small">
                KairosIQ AI Realty is committed to the letter and spirit of U.S. policy 
                for the achievement of equal housing opportunity throughout the Nation.
              </p>
            </div>
          </Col>
        </Row>

        {/* MLS Attribution */}
        <Row className="mls-attribution py-3 border-top">
          <Col>
            <p className="text-center text-muted small mb-0">
              All listing information is owned and provided by KairosIQ AI Realty, a licensed real estate brokerage. 
              The data relating to real estate for sale on this website comes in part from the 
              Internet Data Exchange (IDX) Program of Arizona Regional Multiple Listing Service, Inc. 
              All information deemed reliable but not guaranteed. Property search technology powered by KairosIQ AI.
            </p>
          </Col>
        </Row>

        {/* Bottom Bar */}
        <Row className="footer-bottom py-3 border-top">
          <Col md={6}>
            <p className="mb-0 text-muted">
              © {new Date().getFullYear()} KairosIQ AI Realty · All rights reserved
            </p>
          </Col>
          <Col md={6} className="text-md-end">
            <p className="mb-0 text-muted">
              Technology by <a href="https://kairosiq.ai" target="_blank" rel="noopener noreferrer">KairosIQ AI</a> · 
              Brokerage Services: KairosIQ AI Realty · License #CO721005000
            </p>
          </Col>
        </Row>
      </Container>

      <style>{`
        .chat-footer-enhanced {
          background-color: #f8f9fa;
          color: #333;
          margin-top: 80px;
        }

        .footer-brand h5 {
          color: #333;
          font-weight: 600;
          margin-bottom: 10px;
        }

        .footer-title {
          color: #333;
          font-weight: 600;
          margin-bottom: 15px;
          text-transform: uppercase;
          font-size: 14px;
        }

        .footer-links {
          list-style: none;
          padding: 0;
        }

        .footer-links li {
          margin-bottom: 8px;
        }

        .footer-links a {
          color: #6c757d;
          text-decoration: none;
          transition: color 0.2s;
          font-size: 14px;
        }

        .footer-links a:hover {
          color: #007bff;
        }

        .contact-info p {
          font-size: 14px;
          color: #6c757d;
          margin-bottom: 5px;
        }

        .contact-info svg {
          margin-right: 8px;
          color: #007bff;
        }

        .fair-housing-section {
          text-align: center;
        }

        .fair-housing-logo {
          margin: 0 auto;
        }

        .fair-housing-text {
          font-size: 12px;
          line-height: 1.5;
        }

        .mls-attribution {
          background-color: #e9ecef;
        }

        .footer-bottom {
          font-size: 13px;
        }

        .footer-bottom a {
          color: #007bff;
          text-decoration: none;
        }

        .footer-bottom a:hover {
          text-decoration: underline;
        }
      `}</style>
    </footer>
  );
};

export default FooterWithPolicies;